// FRU-23389 (Ed, 2026-09-29): one monthly-payment calculation for Hope Direct and the Match page, so both show the same
// number. It is the standard fixed-rate amortization formula, principal and interest only. It matches the reviewed rate
// revision's own monthly_payment figures to the dollar (550,000 at 6.374% over 360 months is 3,431; at 5.5% over 120 it
// is 5,968.95).
//
// The payment always uses a 30-year fixed pair (Ed, 2:13 AM: "if you use a 10 year term or 15 the payment will be very
// high"). The landing's headline may show a 10-year rate; the payment never borrows it.

/** Monthly principal and interest, unrounded, or null when an input can't make a payment. */
export function monthlyPrincipalAndInterest(principal: number, annualRatePercent: number, termMonths: number): number | null {
  const p = Number(principal);
  const annual = Number(annualRatePercent);
  const n = Math.round(Number(termMonths));
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(annual) || annual < 0 || annual > 30 || !Number.isFinite(n) || n <= 0) {
    return null;
  }
  const r = annual / 100 / 12;
  if (r === 0) { return p / n; }
  const growth = Math.pow(1 + r, n);
  return (p * r * growth) / (growth - 1);
}

/** "$1,847" for a payment (whole dollars, rounded to nearest). */
export function formatPaymentUSD(payment: number): string {
  return '$' + Math.round(payment).toLocaleString('en-US');
}

export type HopePaymentPurpose = 'refinance' | 'cash_out' | 'purchase';

/** How the loan amount was worked out from the visitor's answers, so the Match page can say it in words. */
export type HopePaymentBasis = 'balance' | 'balance_plus_cash' | 'max_cash' | 'price_minus_down';

/**
 * What Hope Direct leaves in this tab's sessionStorage at submit for the Match page to unlock. Amount only: no name,
 * contact detail or address. Written only for the payments version of the test.
 */
export interface HopePaymentRecord {
  v: 1;
  arm: 'payments';
  purpose: HopePaymentPurpose;
  product: '30yr_fixed';
  loanAmount: number;
  basis: HopePaymentBasis;
  at: number;
  /** The lead id the thank-you URL carries. The Match page unlocks a record only for its own lead. */
  lead?: string;
  /**
   * The payment Hope Direct calculated, and exactly what it used: the reviewed 30-year pair (rate and APR as the revision
   * gives them), its term, and the revision and offer it came from. Present only when Hope had that pair; the Match page
   * shows these numbers rather than refetching a rate that may have moved.
   */
  priced?: HopePaymentPricing;
}

export interface HopePaymentPricing {
  rate: string;
  apr: string;
  termMonths: 360;
  revisionId: string;
  offerSha256: string;
  payment: number;   // whole dollars
  /** The pair's own advertised-rate disclosure (signed, per revision), so the Match page links it without a fetch. */
  disclosureUrl?: string;
}

/** A disclosure link is kept only if it is the rate API's own https advertised-rate-disclosure page. */
export function hopePaymentDisclosureUrl(raw: any): string {
  if (typeof raw !== 'string' || !raw) { return ''; }
  try {
    const u = new URL(raw);
    const host = u.hostname.toLowerCase();
    if (u.protocol !== 'https:' || !(host === 'freerateupdate.com' || host.endsWith('.freerateupdate.com'))) { return ''; }
    return u.pathname.indexOf('/advertised-rate-disclosure/') === 0 ? u.toString() : '';
  } catch (e) { return ''; }
}

/** Prices a loan on a reviewed 30-year pair, or null when the pair or the amount can't make a payment. */
export function hopePaymentPricing(loanAmount: number, pair: { rate: any; apr: any; revision_id?: any; offer_sha256?: any; disclosure?: any; disclosureUrl?: any } | null): HopePaymentPricing | null {
  if (!pair) { return null; }
  const rate = pair.rate == null ? '' : String(pair.rate).trim();
  const apr = pair.apr == null ? '' : String(pair.apr).trim();
  if (!/^\d{1,2}(\.\d+)?$/.test(rate) || !/^\d{1,2}(\.\d+)?$/.test(apr)) { return null; }
  const payment = monthlyPrincipalAndInterest(loanAmount, Number(rate), 360);
  if (payment === null) { return null; }
  const out: HopePaymentPricing = {
    rate, apr, termMonths: 360,
    revisionId: pair.revision_id == null ? '' : String(pair.revision_id),
    offerSha256: pair.offer_sha256 == null ? '' : String(pair.offer_sha256),
    payment: Math.round(payment),
  };
  const disclosureUrl = hopePaymentDisclosureUrl(pair.disclosureUrl !== undefined ? pair.disclosureUrl : (pair.disclosure && pair.disclosure.url));
  if (disclosureUrl) { out.disclosureUrl = disclosureUrl; }
  return out;
}

export const HOPE_PAYMENT_STORAGE_KEY = 'hope_payment_v1';

/** A record older than this is ignored (a later visit in the same tab is a new question). */
export const HOPE_PAYMENT_MAX_AGE_MS = 2 * 60 * 60 * 1000;

/** The reviewed rate bucket the payment uses for a purpose. */
export function hopePaymentRateBucket(purpose: HopePaymentPurpose): { purpose: HopePaymentPurpose; product: '30yr_fixed' } {
  return { purpose, product: '30yr_fixed' };
}

function storedPairLooksReviewed(p: any): boolean {
  const rate = Number(p.rate);
  const apr = Number(p.apr);
  return Number.isFinite(rate) && Number.isFinite(apr) && rate > 0 && rate <= 20 && apr >= rate && apr <= 30
    && typeof p.revisionId === 'string' && /^rates-\d{4}-\d{2}-\d{2}(?:-[a-z0-9-]+)?$/.test(p.revisionId)
    && typeof p.offerSha256 === 'string' && /^[0-9a-f]{64}$/.test(p.offerSha256);
}

/** Parses and checks a stored record, or null if it is missing, malformed or stale. */
export function readHopePaymentRecord(raw: string | null, now: number): HopePaymentRecord | null {
  if (!raw) { return null; }
  let rec: any;
  try { rec = JSON.parse(raw); } catch (e) { return null; }
  if (!rec || typeof rec !== 'object') { return null; }
  if (rec.v !== 1 || rec.arm !== 'payments' || rec.product !== '30yr_fixed') { return null; }
  if (['refinance', 'cash_out', 'purchase'].indexOf(rec.purpose) < 0) { return null; }
  if (['balance', 'balance_plus_cash', 'max_cash', 'price_minus_down'].indexOf(rec.basis) < 0) { return null; }
  const amount = Number(rec.loanAmount);
  if (!Number.isFinite(amount) || amount <= 0 || amount > 10000000) { return null; }
  const at = Number(rec.at);
  if (!Number.isFinite(at) || at > now + 60000 || now - at > HOPE_PAYMENT_MAX_AGE_MS) { return null; }
  const out: HopePaymentRecord = { v: 1, arm: 'payments', purpose: rec.purpose, product: '30yr_fixed', loanAmount: Math.round(amount), basis: rec.basis, at };
  if (rec.lead !== undefined && rec.lead !== null && String(rec.lead).trim() !== '') { out.lead = String(rec.lead).trim(); }
  // A stored price is kept only if it is exactly what the formula gives for this amount at its own rate and term, and its
  // pair still looks like a reviewed one: an APR at or above the rate, a reviewed revision id and a 64-hex offer hash
  // (FRU-23777 review: a corrupted record with APR 0 or below the rate kept its quote). Otherwise the page treats the record
  // as unpriced: the guarded reviewed-rate fallback, or no payment at all.
  const p = rec.priced;
  if (p && typeof p === 'object' && p.termMonths === 360 && storedPairLooksReviewed(p)) {
    const again = hopePaymentPricing(out.loanAmount, { rate: p.rate, apr: p.apr, revision_id: p.revisionId, offer_sha256: p.offerSha256, disclosureUrl: p.disclosureUrl });
    if (again && Number(p.payment) === again.payment && again.revisionId) { out.priced = again; }
  }
  return out;
}
